import { useTranslation } from "react-i18next";
import { Upload } from "antd";
import { AppAvatar, AppButton, GroupTag } from "@/shared/Components";
import { MobileActionsMenu } from "@/common/MobileActionsMenu";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { currentPersonSelector, setContactsIsEdit } from "@/pages/ContactsPage/slice";
import { useModal } from "@/features/modals/lib";
import { ModalDeleteContent } from "@/features/modals";
import { FormButtonSelect } from "@/shared/Components/Form/FormButtonSelect";
import { TitleField } from "@/pages/ContactsPage/Tabs/GeneralInfoTab/Components/Fields";
import { RefreshIcon, TrashBinIcon } from "@/shared/icons";
import { handleFullName } from "@/shared/utils/handle-contacts-null";
import type { Group } from "@/shared/Components";
import type { PersonInfoCardProps } from "../types";
import { Edit } from "../Edit";
import { View } from "../View";
import { usePersonAvatar } from "../Components/PersonAvatar";
import styles from "./styles.module.scss";

export const PersonInfoCardMobile: React.FC<PersonInfoCardProps> = ({
  content,
  isEdit,
  showMobileMenu,
  showEdit,
  form,
}) => {
  const { t } = useTranslation();

  const { handleDelete, handleUpload } = usePersonAvatar();

  const { hideModal, isModalOpen } = useModal();
  const dispatch = useAppDispatch();

  const currentPerson = useAppSelector(currentPersonSelector);

  const editHandler = () => {
    dispatch(setContactsIsEdit(true));
  };

  return (
    <div className={styles["card-content"]}>
      {showEdit && (
        <div className={styles["edit-switch"]} onClick={() => dispatch(setContactsIsEdit(!isEdit))}>
          {isEdit ? t("common.cancel") : t("common.edit")}
        </div>
      )}
      <div className={styles["card-inner"]}>
        <AppAvatar
          image={content.image}
          avatarSize="large"
          name={handleFullName(currentPerson?.firstName, currentPerson?.lastName)}
          className={styles.image}
        />
        {content.group && <GroupTag group={content.group as Group} className={styles.tag} />}
        {isEdit && form && (
          <div className={styles["avatar-manage-buttons"]}>
            <Upload
              openFileDialogOnClick
              beforeUpload={(file) => handleUpload({ file })}
              showUploadList={false}
            >
              <AppButton className={styles["change-photo-button"]} type="text">
                <RefreshIcon className={styles["refresh-icon"]} />
                {t("common.photo.changePhoto")}
              </AppButton>
            </Upload>
            <AppButton onClick={handleDelete} className={styles["delete-photo-button"]} type="text">
              <TrashBinIcon />
              {t("common.photo.deletePhoto")}
            </AppButton>
          </div>
        )}
      </div>
      {isEdit && form && (
        <FormButtonSelect
          placeholder={t("contactsPage.group")}
          name="group"
          options={[
            { label: t("contactsPage.groups.other"), value: "Other" },
            { label: t("contactsPage.groups.business"), value: "Business" },
            { label: t("contactsPage.groups.friends"), value: "Friends" },
            { label: t("contactsPage.groups.colleagues"), value: "Colleagues" },
            { label: t("contactsPage.groups.family"), value: "Family" },
          ]}
        />
      )}
      {isEdit && form && <TitleField />}
      {isEdit && form ? (
        <Edit />
      ) : (
        <>
          <View content={content} layout="mobile" />
          {showMobileMenu && (
            <MobileActionsMenu onEdit={editHandler} className={styles["profile-menu"]} />
          )}
        </>
      )}
      <ModalDeleteContent
        open={isModalOpen}
        onOk={hideModal}
        onCancel={hideModal}
        modalTitle={t("contactsPage.deleteModal")}
      />
    </div>
  );
};
